import { Timestamp } from "@bufbuild/protobuf";
import { useQuery } from "@tanstack/react-query";

import React, { useContext, useEffect } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, LinearProgress } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFooter,
  GridFooterContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { Gateway } from "@safetyculture/s12-apis-connect-web/s12/sensors/v1/gateway_pb";

import { convertTimestampToDate } from "../../../../helpers/time";
import { gatewaysOptions } from "../../../../queries/gateways";
import { UrlParamTypes } from "../../../../types/org-display";
import {
  ConnectionStatusChip,
  ConnectionTypeChip,
} from "components/sensors/table/chips";
import { ColumnHeader } from "components/sensors/table/column-header";
import CopyableCell from "components/sensors/table/copyable-cell";
import {
  DATE_FORMAT,
  copyableDateCellProps,
  localTimezoneLabel,
} from "components/sensors/table/copyable-date";
import { MonoText } from "components/sensors/table/mono-text";
import { NoResultsOverlay } from "components/sensors/table/no-results-overlay";
import {
  NO_VALUE_TEXT_SX,
  noValueText,
} from "components/sensors/table/no-value-text";
import {
  COLUMN_WIDTH,
  CellRow,
  TABLE_HEIGHT,
  dataGridSx,
} from "components/sensors/table/table-layout";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

// Only three connection types actually exist: Test, Ethernet, Cellular.
// Anything else is an unrecognised vendor, not a valid connection type.
export function getConnectionType(id: string, sourceName: string): string {
  const lowerId = id.toLowerCase();
  const lowerSourceName = sourceName.toLowerCase();
  if (lowerId.startsWith("eui-647")) {
    return "Cellular";
  }
  if (lowerId.startsWith("eui-0000")) {
    return "Ethernet";
  }
  if (lowerSourceName === "s12test") {
    return "Test";
  }
  return "Unknown";
}

function getDeviceId(
  displayId: string,
  sourceName: string,
  sourceId: string,
): string {
  const lowerSourceName = sourceName.toLowerCase();
  if (lowerSourceName === "monnit") {
    return sourceId;
  }
  return displayId;
}

export function isGatewayConnected(lastSeenTime: Timestamp): boolean {
  const currentTimeUnix = Math.floor(Date.now() / 1000);
  const lastSeenTimeUnix = Number(lastSeenTime.seconds);

  return lastSeenTimeUnix ? currentTimeUnix - lastSeenTimeUnix < 3600 : false;
}

// Downloads the CSV directly on click, skipping the extra menu step the
// default GridToolbarExport shows for a single format.
function ExportFooter() {
  const apiRef = useGridApiContext();
  return (
    <GridFooterContainer>
      <Button
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => apiRef.current.exportDataAsCsv()}
        sx={{ marginLeft: 1 }}
      >
        Export CSV
      </Button>
      <GridFooter sx={{ border: "none" }} />
    </GridFooterContainer>
  );
}

const OrganisationGatewaysTable = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);
  const [pageSize, setPageSize] = React.useState<number>(100);
  const [page, setPage] = React.useState<number>(0);
  // 100 rows is the maximum number of rows that can be rendered for MIT version of Data Grid

  const { data, isLoading, error } = useQuery(
    gatewaysOptions({ regionCode, roleId }),
  );
  const gatewaysList: Gateway[] = data?.gateways ?? [];

  useEffect(() => {
    if (error) {
      showError(error as GrpcError);
    }
  }, [error, showError]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: COLUMN_WIDTH.name,
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.name} tabIndex={tabIndex} />;
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: COLUMN_WIDTH.location,
      valueGetter: ({ row }) => row.locationName,
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.locationName} tabIndex={tabIndex} />;
      },
    },
    {
      field: "lastSeenTime",
      headerName: "Status",
      width: 140,
      align: "left",
      renderHeader: () => (
        <ColumnHeader label="Status" tooltip="Connection status" />
      ),
      valueGetter({ row }) {
        return isGatewayConnected(row.lastSeenTime);
      },
      valueFormatter({ value }) {
        return value ? "Connected" : "Disconnected";
      },
      renderCell({ row }) {
        return (
          <CellRow>
            <ConnectionStatusChip
              connected={isGatewayConnected(row.lastSeenTime)}
            />
          </CellRow>
        );
      },
    },
    {
      field: "lastSeen",
      headerName: `Last seen (${localTimezoneLabel()})`,
      width: 180,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <ColumnHeader
          label="Last seen"
          tooltip={`Times shown in ${localTimezoneLabel()}`}
        />
      ),
      valueGetter({ row }) {
        const nullTime = new Timestamp({ seconds: BigInt(0), nanos: 0 });
        return row.lastSeenTime.seconds !== nullTime.seconds
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.lastSeenTime),
            ).toFormat(DATE_FORMAT)
          : "";
      },
      renderCell({ row, tabIndex }) {
        const nullTime = new Timestamp({ seconds: BigInt(0), nanos: 0 });
        const hasLastSeen = row.lastSeenTime.seconds !== nullTime.seconds;
        const lastSeenDate = hasLastSeen
          ? DateTime.fromJSDate(convertTimestampToDate(row.lastSeenTime))
          : null;
        return (
          <CellRow>
            <CopyableCell
              {...copyableDateCellProps(lastSeenDate)}
              tabIndex={tabIndex}
            />
          </CellRow>
        );
      },
    },
    {
      field: "type",
      headerName: "Connection type",
      width: 150,
      valueGetter: ({ row }) =>
        getConnectionType(row.displayId, row.sourceName),
      renderCell({ value: connectionType }) {
        return <ConnectionTypeChip connectionType={connectionType} />;
      },
    },
    {
      field: "vendor",
      headerName: "Source name",
      width: COLUMN_WIDTH.sourceName,
      align: "left",
      valueGetter: ({ row }) => {
        return row.sourceName;
      },
      // CSV export reads valueGetter's value directly, not renderCell's --
      // keep it in sync with the "Unknown" fallback shown below.
      valueFormatter: ({ value }) => value || "Unknown",
      renderCell: ({ row, tabIndex }) => {
        if (!row.sourceName) {
          return (
            <Box component="span" sx={NO_VALUE_TEXT_SX}>
              Unknown
            </Box>
          );
        }
        return (
          <CopyableCell value={row.sourceName} tabIndex={tabIndex}>
            <MonoText>{row.sourceName}</MonoText>
          </CopyableCell>
        );
      },
    },
    {
      field: "sourceId",
      headerName: "Source ID",
      width: COLUMN_WIDTH.id,
      align: "left",
      valueGetter: ({ row }) => row.sourceId,
      renderCell({ row, tabIndex }) {
        return (
          <CopyableCell value={row.sourceId} tabIndex={tabIndex}>
            <MonoText>{row.sourceId}</MonoText>
          </CopyableCell>
        );
      },
    },
    {
      field: "displayId",
      headerName: "Display ID",
      width: COLUMN_WIDTH.id,
      valueGetter: ({ row }) =>
        getDeviceId(row.displayId, row.sourceName, row.sourceId),
      renderCell({ value: id, tabIndex }) {
        return (
          <CopyableCell value={id} tabIndex={tabIndex}>
            {id ? <MonoText>{id}</MonoText> : noValueText("-")}
          </CopyableCell>
        );
      },
    },
    {
      field: "sensorCount",
      headerName: "Sensor count",
      width: 150,
      align: "left",
      renderHeader: () => (
        <ColumnHeader
          label="Sensor count"
          tooltip="Number of sensors that last sent a reading via this gateway"
        />
      ),
      valueGetter: ({ row }) => {
        return row.connectedSensors.length;
      },
      renderCell: ({ row }) => {
        return <Box>{row.connectedSensors.length}</Box>;
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: TABLE_HEIGHT, width: "100%" }}>
        <DataGrid
          rows={gatewaysList}
          columns={columns}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.sourceId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : NoResultsOverlay,
            Footer: ExportFooter,
          }}
          data-testid="org-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            sorting: {
              sortModel: [{ field: "location", sort: "asc" }],
            },
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          sx={dataGridSx}
        />
      </Box>
    </>
  );
};

export default OrganisationGatewaysTable;
